// ============================================================
// TMR Portal Admin — PT-BR wording for the authorization vocabulary
// ============================================================
import { useMemo } from 'react';
import type { AccessRequestStatus, AuditAction, CatalogFeature, InviteDelivery } from './contract';
import { useFeatureCatalog } from './queries';

/**
 * Names a permission key in the words the screen shows, from the catalogue the backend publishes.
 *
 * The wording used to be a map in this file, and that was the bug: permissions are added by migration
 * (06 §5.2), so every new key needed a second edit here to stop rendering as `company.rates.manage` on a
 * checkbox. Four of them shipped without it. `Permission.name` now travels with the key, and this hook
 * only indexes what already arrives.
 *
 * A key the catalogue does not carry falls back to the key itself — while the catalogue loads, and for a
 * key that somehow reaches a screen without being catalogued. Showing it raw is honest; hiding it would
 * silently make it ungrantable.
 *
 * @returns A resolver from permission key to display name.
 */
export function usePermissionName(): (key: string) => string {
  const catalog = useFeatureCatalog();
  const names = useMemo(() => indexPermissionNames(catalog.data), [catalog.data]);

  return useMemo(() => (key: string) => names.get(key) ?? key, [names]);
}

/**
 * Flattens the feature-grouped catalogue into a key → name index.
 *
 * Exported for the sake of testing the fallback without a query client; screens use
 * {@link usePermissionName}.
 *
 * @param features The catalogue, or undefined while it loads.
 */
export function indexPermissionNames(features: CatalogFeature[] | undefined): Map<string, string> {
  return new Map(
    (features ?? []).flatMap((feature) => feature.permissions.map((p) => [p.key, p.name] as const)),
  );
}

/**
 * The audited entity names, as the backend writes them (its CLR type names) — the exact-match filter
 * value on `GET /authorization/audit-log` and the label above each row.
 */
const AUDIT_ENTITY_LABELS: Record<string, string> = {
  Role: 'Papel',
  Permission: 'Permissão',
  RolePermission: 'Permissões do papel',
  UserRoleAssignment: 'Atribuição de papel',
  Feature: 'Módulo',
};

/** The entities the audit log records, for the filter — in the order the filter offers them. */
export const AUDIT_ENTITIES = Object.keys(AUDIT_ENTITY_LABELS);

/**
 * The operator-facing name of an audited entity, falling back to the raw name so an entity added to the
 * interceptor later still reads as something rather than disappearing from the filter.
 *
 * @param entity The entity name as recorded in the log.
 */
export function auditEntityLabel(entity: string): string {
  return AUDIT_ENTITY_LABELS[entity] ?? entity;
}

const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  created: 'Criado',
  updated: 'Alterado',
  deleted: 'Excluído',
};

/**
 * The operator-facing name of an audited action.
 *
 * @param action The action as recorded in the log.
 */
export function auditActionLabel(action: AuditAction): string {
  return AUDIT_ACTION_LABELS[action] ?? action;
}

/**
 * Wording for the fields inside an audit snapshot. The keys are the backend's entity property names —
 * the interceptor serializes `property.Metadata.Name` verbatim (10 B7c), so they arrive Pascal-cased.
 */
const AUDIT_FIELD_LABELS: Record<string, string> = {
  Id: 'Id',
  Name: 'Nome',
  IsSystem: 'Papel de sistema',
  ParentRoleId: 'Papel pai',
  PermissionKey: 'Permissão',
  RoleId: 'Papel',
  PermissionId: 'Permissão',
  UserId: 'Usuário',
  CompanyId: 'Empresa',
  HoldingId: 'Holding',
  FeatureId: 'Módulo',
  System: 'Portal',
  ExpiresAt: 'Validade',
  RevokedAt: 'Revogado em',
  CreatedAt: 'Criado em',
  UpdatedAt: 'Atualizado em',
};

/**
 * The operator-facing name of a field inside an audit snapshot, falling back to the raw property name —
 * the snapshot is shaped by whatever the backend entity holds, so this map can never be exhaustive.
 *
 * @param field The property name as recorded in the snapshot.
 */
export function auditFieldLabel(field: string): string {
  return AUDIT_FIELD_LABELS[field] ?? field;
}

/** Wording for each state of an access request, matching the tabs of the approval queue. */
const ACCESS_REQUEST_STATUS_LABELS: Record<string, string> = {
  Pending: 'Aguardando',
  Approved: 'Aprovada',
  Rejected: 'Rejeitada',
};

/**
 * The operator-facing name of an access request status.
 *
 * @param status The status as the backend spells it (Pascal case).
 */
export function accessRequestStatusLabel(status: string): string {
  return ACCESS_REQUEST_STATUS_LABELS[status] ?? status;
}

/**
 * The key a badge renders for one point of the invitation's life. Kept apart from the wire values because
 * it is neither of them: it is the two of them read together.
 */
export type InviteSituation =
  | 'convite_aguardando'
  | 'convite_fila'
  | 'convite_enviado'
  | 'convite_criado'
  | 'convite_falhou'
  | 'convite_rejeitado';

/**
 * Where somebody actually is, from the decision and the delivery together.
 *
 * The queue used to show the decision alone, so registering a user read "Aprovada" the instant the
 * operator clicked — while the invitation had only just been posted and the person had done nothing.
 * The ladder that matters to whoever is watching is **Enviado → Criado**, and neither rung is a decision:
 * one is an e-mail leaving, the other is somebody acting on it.
 *
 * @param status The operator's decision.
 * @param delivery What became of the Clerk invitation.
 */
export function inviteSituation(
  status: AccessRequestStatus,
  delivery: InviteDelivery,
): InviteSituation {
  if (status === 'Rejected') return 'convite_rejeitado';
  if (status === 'Pending') return 'convite_aguardando';

  // Approved: the decision is settled and the invitation is the story from here.
  switch (delivery) {
    case 'Accepted':
      return 'convite_criado';
    case 'Sent':
      return 'convite_enviado';
    case 'Abandoned':
      return 'convite_falhou';
    default:
      // `Pending` and `NotRequired` both mean nobody has been e-mailed yet. The second should not occur
      // on an approved row, and reading it as "queued" is the safe way to be wrong: it says somebody is
      // still waiting, which is the state an operator needs to look at.
      return 'convite_fila';
  }
}

/**
 * Whether "Reenviar convite" applies: an approved request whose invitation is out, queued or given up on.
 * Mirrors the backend rule (`AccessRequest.ReissueInvite`), so the button is never offered for a refusal.
 *
 * Not for an accepted invitation — the person is in, and there is nothing to resend — nor for a request
 * nobody approved, which has no invitation behind it.
 *
 * @param status The operator's decision.
 * @param delivery What became of the invitation.
 */
export function canReissueInvite(status: AccessRequestStatus, delivery: InviteDelivery): boolean {
  return status === 'Approved' && (delivery === 'Pending' || delivery === 'Sent' || delivery === 'Abandoned');
}
